import { ReactNode, useEffect, useRef, useState } from 'react';
import * as R from 'remeda';
import { IconDotsVertical } from '@rossum/ui/icons/tabler';
import {
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';

export type SidebarAction = {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  dataCy?: string;
};

export type SidebarActionBarProps = {
  actions: SidebarAction[];
  /** Rendered in the second slot, fills whatever the actions leave over. */
  children?: ReactNode;
  /** Renders skeletons in the action slot instead of the actions. */
  loading?: boolean;
};

const SIDEBAR_ACTION_BUTTON_SIZE = 35;

// 35px width + 8px spacing
const SIDEBAR_ACTION_BUTTON_WIDTH = SIDEBAR_ACTION_BUTTON_SIZE + 8;

export const SidebarActionBar = ({
  actions,
  children,
  loading = false,
}: SidebarActionBarProps) => {
  const barRef = useRef<HTMLDivElement | null>(null);

  const [visibleCount, setVisibleCount] = useState(4);

  const resizeObserverRef = useRef<ResizeObserver>(
    new ResizeObserver(([entry]) => {
      const contentBox = entry ? entry.contentBoxSize[0] : null;
      if (contentBox) {
        // The actions may claim at most half of the bar; the rest belongs to
        // the fill slot, which is why the fill slot never gets thinner than 50%.
        const availableSpace = contentBox.inlineSize / 2;
        const noOfActions = Math.floor(
          availableSpace / SIDEBAR_ACTION_BUTTON_WIDTH
        );

        setVisibleCount(count => (noOfActions !== count ? noOfActions : count));
      }
    })
  );

  useEffect(() => {
    const resizeObserver = resizeObserverRef.current;
    const bar = barRef.current;

    if (bar) {
      resizeObserver.observe(bar);
    }

    return () => {
      if (bar) {
        resizeObserver.unobserve(bar);
      }
    };
  }, []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);

  // -1 is for the "More" button
  const [visibleActions, contextActions] = R.partition(
    actions,
    (_, index) => index < visibleCount - 1
  );

  return (
    <Stack ref={barRef} direction="row" spacing={1} sx={{ width: '100%' }}>
      {loading ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{ flex: '0 0 auto', alignItems: 'center' }}
          data-cy="sidebar-action-bar-skeleton"
        >
          {R.range(0, visibleCount).map(item => (
            <Skeleton
              key={item}
              variant="circular"
              sx={{
                flex: '0 0 auto',
                width: SIDEBAR_ACTION_BUTTON_SIZE,
                height: SIDEBAR_ACTION_BUTTON_SIZE,
              }}
            />
          ))}
        </Stack>
      ) : (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flex: '0 0 auto',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {contextActions.length ? (
            <>
              <IconButton
                size="medium"
                color="secondary"
                onClick={e => setAnchorEl(e.currentTarget)}
                data-cy="sidebar-footer-actions-context-menu-button"
              >
                <SvgIcon component={IconDotsVertical} fontSize="medium" />
              </IconButton>
              <Menu
                open={!!anchorEl}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setAnchorEl(null)}
              >
                {contextActions.map(action => (
                  <MenuItem
                    key={action.id}
                    onClick={() => {
                      action.onClick();
                      setAnchorEl(null);
                    }}
                    disabled={!!action.disabled}
                    data-cy={action.dataCy}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {action.icon}
                      <Typography>{action.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : null}
          <Stack direction="row-reverse" spacing={1}>
            {visibleActions.map(action => (
              <Tooltip key={action.id} title={action.label}>
                <span>
                  <IconButton
                    size="medium"
                    color="secondary"
                    onClick={action.onClick}
                    disabled={!!action.disabled}
                    aria-label={action.label}
                    data-cy={action.dataCy}
                  >
                    {action.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </Stack>
        </Stack>
      )}
      <Stack sx={{ flex: '1 1 0', minWidth: 0 }}>{children}</Stack>
    </Stack>
  );
};
